/**
 * SEO Configuration
 * Centralized SEO settings for the application
 */

export const SEO_CONFIG = {
  siteName: 'MahStry',
  siteUrl: 'https://mahstry.com',
  defaultTitle: "MahStry - India's #1 Online Study Platform | Mock Tests, Courses, Exam Preparation",
  defaultDescription: "MahStry is India's leading online study platform for competitive exams. Practice mock tests, join study rooms, access courses, check results & admit cards. Prepare for CAT, JEE, NEET, UPSC, SSC, Banking & more with expert guidance.",
  defaultKeywords: [
    'MahStry',
    'online study platform',
    'mock tests',
    'competitive exams',
    'exam preparation',
    'CAT preparation',
    'JEE preparation',
    'NEET preparation',
    'UPSC preparation',
    'SSC preparation',
    'banking exams',
    'study platform India',
    'online courses',
    'test series',
    'practice tests',
    'exam coaching',
    'study materials',
    'previous year papers',
    'sample papers',
    'study groups',
    'collaborative learning',
    'exam tips',
    'study strategies',
    'performance analytics',
    'exam calendar',
    'notification',
    'syllabus',
    'study plan',
    'revision notes',
    'flashcards',
    'doubt resolution',
    'expert guidance',
    'exam coaching online',
    'best study app',
    'top education platform',
  ],
  defaultImage: 'https://mahstry.com/icon-512x512.png',
  ogImage: 'https://mahstry.com/og-image.jpg',
  twitterImage: 'https://mahstry.com/twitter-image.jpg',
  twitterHandle: '@mahstry',
  facebookPage: 'https://facebook.com/mahstry',
  linkedinPage: 'https://linkedin.com/company/mahstry',
  instagramPage: 'https://instagram.com/mahstry',
  youtubeChannel: 'https://youtube.com/mahstry',
  contactEmail: 'support@mahstry.com',
  locale: 'en_IN',
  alternateLocales: ['hi_IN'],
  themeColor: '#2563eb',
};

// Page-specific SEO configurations
export const PAGE_SEO = {
  home: {
    title: "MahStry - India's #1 Online Study Platform | Mock Tests, Courses, Exam Preparation",
    description: "India's leading online study platform for competitive exams. Practice mock tests, join study rooms, access courses, check results & admit cards. Prepare for CAT, JEE, NEET, UPSC, SSC, Banking & more.",
    keywords: ['online study', 'mock tests', 'competitive exams', 'exam preparation', 'study platform India'],
    priority: 1.0,
    changefreq: 'daily',
  },
  dashboard: {
    title: 'Dashboard | MahStry',
    description: 'Access your mock tests, courses, study rooms, and performance analytics on MahStry dashboard.',
    keywords: ['dashboard', 'student dashboard', 'exam dashboard', 'study dashboard'],
    priority: 0.9,
    changefreq: 'daily',
  },
  mockTests: {
    title: 'Mock Tests for Competitive Exams | MahStry',
    description: 'Practice mock tests for CAT, JEE, NEET, UPSC, SSC, Banking and more competitive exams. Get detailed analytics and performance insights.',
    keywords: ['mock tests', 'practice tests', 'competitive exam tests', 'CAT mock test', 'JEE mock test', 'NEET mock test'],
    priority: 0.9,
    changefreq: 'daily',
  },
  courses: {
    title: 'Online Courses for Competitive Exams | MahStry',
    description: 'Comprehensive online courses for CAT, JEE, NEET, UPSC, SSC, Banking exams. Learn from expert instructors and track your progress.',
    keywords: ['online courses', 'exam courses', 'competitive exam courses', 'CAT course', 'JEE course', 'NEET course'],
    priority: 0.9,
    changefreq: 'daily',
  },
  studyRooms: {
    title: 'Study Rooms - Collaborative Learning | MahStry',
    description: 'Join study rooms for collaborative learning sessions. Study with peers, share notes, and prepare together for competitive exams.',
    keywords: ['study rooms', 'collaborative learning', 'group study', 'online study rooms', 'peer learning'],
    priority: 0.8,
    changefreq: 'daily',
  },
  results: {
    title: 'Exam Results & Admit Cards | MahStry',
    description: 'Check exam results and download admit cards for various competitive exams. Stay updated with latest exam notifications.',
    keywords: ['exam results', 'admit cards', 'result check', 'exam notification', 'competitive exam results'],
    priority: 0.9,
    changefreq: 'daily',
  },
  contact: {
    title: 'Contact Us | MahStry',
    description: 'Get in touch with MahStry support team. We are here to help you with your exam preparation journey.',
    keywords: ['contact', 'support', 'help', 'customer service', 'MahStry contact'],
    priority: 0.7,
    changefreq: 'monthly',
  },
  about: {
    title: 'About MahStry | India\'s Leading Study Platform',
    description: 'Learn about MahStry, India\'s leading online study platform for competitive exams. Our mission, vision, and commitment to student success.',
    keywords: ['about', 'MahStry about', 'study platform about', 'education platform'],
    priority: 0.8,
    changefreq: 'monthly',
  },
  privacy: {
    title: 'Privacy Policy | MahStry',
    description: 'Read MahStry\'s privacy policy to understand how we collect, use, and protect your personal information.',
    keywords: ['privacy policy', 'data protection', 'privacy'],
    priority: 0.5,
    changefreq: 'yearly',
  },
  terms: {
    title: 'Terms of Service | MahStry',
    description: 'Read MahStry\'s terms of service to understand the rules and guidelines for using our platform.',
    keywords: ['terms of service', 'terms and conditions', 'user agreement'],
    priority: 0.5,
    changefreq: 'yearly',
  },
};

// Exam-specific SEO configurations
export const EXAM_SEO = {
  cat: {
    title: 'CAT Exam Preparation | Mock Tests & Courses | MahStry',
    description: 'Comprehensive CAT exam preparation with mock tests, courses, study materials, and expert guidance. Prepare for Common Admission Test with MahStry.',
    keywords: ['CAT exam', 'CAT preparation', 'CAT mock test', 'Common Admission Test', 'CAT 2025'],
  },
  jee: {
    title: 'JEE Main & Advanced Preparation | Mock Tests & Courses | MahStry',
    description: 'Complete JEE Main and JEE Advanced preparation with mock tests, courses, previous year papers, and study materials. Ace your JEE exam with MahStry.',
    keywords: ['JEE Main', 'JEE Advanced', 'JEE preparation', 'JEE mock test', 'JEE 2025'],
  },
  neet: {
    title: 'NEET Exam Preparation | Mock Tests & Courses | MahStry',
    description: 'Comprehensive NEET exam preparation with mock tests, courses, study materials, and expert guidance. Prepare for National Eligibility cum Entrance Test.',
    keywords: ['NEET exam', 'NEET preparation', 'NEET mock test', 'NEET 2025', 'medical entrance'],
  },
  upsc: {
    title: 'UPSC Exam Preparation | Mock Tests & Courses | MahStry',
    description: 'Complete UPSC Civil Services exam preparation with mock tests, courses, study materials, and expert guidance. Prepare for IAS, IPS, and other civil services.',
    keywords: ['UPSC', 'IAS preparation', 'civil services', 'UPSC mock test', 'UPSC 2025'],
  },
  ssc: {
    title: 'SSC Exam Preparation | Mock Tests & Courses | MahStry',
    description: 'Comprehensive SSC exam preparation for CGL, CHSL, MTS, and other SSC exams. Practice mock tests and access study materials.',
    keywords: ['SSC CGL', 'SSC CHSL', 'SSC preparation', 'SSC mock test', 'SSC 2025'],
  },
  banking: {
    title: 'Banking Exam Preparation | IBPS, SBI, RBI | MahStry',
    description: 'Complete banking exam preparation for IBPS, SBI, RBI, and other banking exams. Practice mock tests and access comprehensive study materials.',
    keywords: ['banking exams', 'IBPS', 'SBI', 'RBI', 'banking preparation', 'banking mock test'],
  },
};

/**
 * Generate SEO metadata for a page
 */
export const generatePageSEO = (pageKey, customData = {}) => {
  const pageConfig = PAGE_SEO[pageKey] || {};
  return {
    title: customData.title || pageConfig.title || SEO_CONFIG.defaultTitle,
    description: customData.description || pageConfig.description || SEO_CONFIG.defaultDescription,
    keywords: customData.keywords || pageConfig.keywords || SEO_CONFIG.defaultKeywords,
    image: customData.image || SEO_CONFIG.defaultImage,
    canonical: customData.canonical || `${SEO_CONFIG.siteUrl}${customData.path || ''}`,
    ...customData,
  };
};

/**
 * Generate exam-specific SEO metadata
 */
export const generateExamSEO = (examKey, customData = {}) => {
  const examConfig = EXAM_SEO[examKey] || {};
  return {
    title: customData.title || examConfig.title || SEO_CONFIG.defaultTitle,
    description: customData.description || examConfig.description || SEO_CONFIG.defaultDescription,
    keywords: customData.keywords || examConfig.keywords || SEO_CONFIG.defaultKeywords,
    image: customData.image || SEO_CONFIG.defaultImage,
    canonical: customData.canonical || `${SEO_CONFIG.siteUrl}/exams/${examKey}`,
    ...customData,
  };
};

